// src/components/GeneratePhotoshootModal.jsx
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Dialog } from "@headlessui/react";
import { X, Image as ImageIcon, Plus, Trash2, Loader2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";
import Loader from "./Loader"; // ✅ same loader concept

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ecom-ads-ai-backend-production.up.railway.app";

const SIZES = [
  { label: "Post (1080x1080)", value: "1080x1080" },
  { label: "Landscape (1200x628)", value: "1200x628" },
  { label: "Story (1080x1920)", value: "1080x1920" },
  { label: "Portrait (1080x1350)", value: "1080x1350" },
  { label: "Pin (1000x1500)", value: "1000x1500" },
];

// Single-step header
const steps = [{ number: 1, label: "Select Image" }];

function StepperHeader({ step }) {
  return (
    <div className="flex items-center gap-2 px-8 pt-7 pb-2">
      {steps.map((s, idx) => (
        <React.Fragment key={s.number}>
          <span
            className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold ${
              step === s.number
                ? "bg-pink-600 text-white"
                : "bg-[#29294a] text-gray-400"
            }`}
          >
            {s.number}
          </span>
          <span
            className={`text-base sm:text-lg font-semibold ${
              step === s.number ? "text-white" : "text-gray-400"
            } ${idx !== steps.length - 1 ? "mr-8" : ""}`}
          >
            {s.label}
          </span>
        </React.Fragment>
      ))}
    </div>
  );
}

function StepFooter({ onGenerate, canGenerate }) {
  return (
    <div className="absolute bottom-0 left-0 w-full bg-[#191930] border-t border-[#23233a] flex justify-end items-center px-8 py-4 z-20">
      <button
        type="button"
        className="px-7 py-2 bg-pink-600 hover:bg-pink-700 rounded-xl font-bold text-white disabled:opacity-40 disabled:cursor-not-allowed"
        onClick={onGenerate}
        disabled={!canGenerate}
      >
        Generate
      </button>
    </div>
  );
}

function AspectRatioPicker({ selected, onSelect }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-2">
      {SIZES.map(({ label, value }) => (
        <button
          key={value}
          type="button"
          onClick={() => onSelect(value)}
          className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition min-h-[92px] ${
            selected === value
              ? "border-pink-500 bg-[#2A2A40]"
              : "border-transparent bg-[#1F1F2F] hover:border-gray-600"
          }`}
        >
          <ImageIcon className="mb-2" size={28} />
          <span className="font-semibold text-sm">{label.split(" (")[0]}</span>
          <span className="text-xs text-gray-400">
            {label.split(" (")[1]?.replace(")", "")}
          </span>
        </button>
      ))}
    </div>
  );
}

export default function GeneratePhotoshootModal({
  productImages = [],
  imagesLoading = false, // ✅ optional
  imagesError = "", // ✅ optional
  onClose,
  onGenerate,
  hideSizeSelector = false,
}) {
  const { getToken } = useAuth();

  // Single-step: keep a constant step=1 only for header styling
  const step = 1;

  // Normalize: accept string[] or {url}[]
  const normalizedImages = useMemo(() => {
    const arr = Array.isArray(productImages) ? productImages : [];
    return arr
      .map((img) => (typeof img === "string" ? img : img?.url))
      .filter((u) => typeof u === "string" && u);
  }, [productImages]);

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedSize, setSelectedSize] = useState(SIZES[0].value);

  // ✅ Manual upload flow (same as Ad Marketing Video Step 1)
  const [step1UploadedAsset, setStep1UploadedAsset] = useState(null); // { id, url } | null
  const [step1UploadLoading, setStep1UploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const step1FileInputRef = useRef(null);

  const handleStep1Upload = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setStep1UploadLoading(true);
      setUploadError("");

      try {
        if (file.size > 20 * 1024 * 1024) {
          setUploadError("Image is too large. Please upload a file under 20MB.");
          return;
        }

        const token = await getToken();
        const formData = new FormData();
        formData.append("file", file);

        // Reuse existing upload endpoint (same as Ad Marketing Video)
        const { data } = await axios.post(
          `${BACKEND_URL}/api/videos/marketing/endcard/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const url = data?.url;
        if (url) {
          const newAsset = { id: `asset-upload-${Date.now()}`, url };
          setStep1UploadedAsset(newAsset);
          setSelectedImage(url);
        }
      } catch {
        setUploadError("Failed to upload image.");
      } finally {
        setStep1UploadLoading(false);
        event.target.value = "";
      }
    },
    [getToken]
  );

  const handleGenerate = () => {
    if (!selectedImage || imagesLoading || step1UploadLoading) return;
    onGenerate(selectedImage, hideSizeSelector ? undefined : selectedSize);
    onClose();
  };

  const canGenerate = !!selectedImage && !imagesLoading && !step1UploadLoading;

  const isUploadedSelected = useMemo(() => {
    if (!step1UploadedAsset) return false;
    return selectedImage === step1UploadedAsset.url;
  }, [selectedImage, step1UploadedAsset]);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />

      <div className="relative bg-[#191930] text-white rounded-2xl shadow-2xl w-full max-w-[880px] min-h-[410px] h-[82vh] flex flex-col overflow-hidden">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close modal"
          className="absolute top-4 right-4 bg-white text-black p-2 rounded-full hover:bg-gray-100 z-10"
        >
          <X size={22} />
        </button>

        {/* Header */}
        <div className="flex items-center justify-between">
          <StepperHeader step={step} />
        </div>

        {/* Main content (single step) */}
        <div className="flex-1 px-8 py-6 overflow-y-auto pb-44">
          <h2 className="text-xl font-bold text-white mb-3">
            Choose a product image
          </h2>

          <input
            ref={step1FileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleStep1Upload}
          />

          {imagesLoading ? (
            <div className="flex items-center justify-center py-10 sm:py-16">
              <Loader />
            </div>
          ) : normalizedImages.length === 0 && !step1UploadedAsset ? (
            <div className="text-gray-400 text-center py-10">
              No product images found. Analyze your product to extract images first.
            </div>
          ) : (
            // ✅ 5 columns + same card sizing; includes Upload tile
            <div className="grid grid-cols-5 gap-4">
              {/* ✅ Upload tile (becomes a normal selectable image once uploaded) */}
              <div
                className={`relative rounded-lg overflow-hidden border-2 group transition ${
                  step1UploadedAsset
                    ? isUploadedSelected
                      ? "border-pink-500 ring-2 ring-pink-600 cursor-pointer"
                      : "border-transparent hover:border-pink-400 cursor-pointer"
                    : "border-[#3A3A4A] border-dashed hover:border-pink-400 cursor-pointer"
                }`}
                onClick={() => {
                  if (step1UploadLoading || imagesLoading) return;

                  if (step1UploadedAsset) {
                    setSelectedImage(step1UploadedAsset.url);
                    return;
                  }

                  step1FileInputRef.current?.click();
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (step1UploadLoading || imagesLoading) return;

                    if (step1UploadedAsset) {
                      setSelectedImage(step1UploadedAsset.url);
                      return;
                    }

                    step1FileInputRef.current?.click();
                  }
                }}
                aria-label={step1UploadedAsset ? "Uploaded image" : "Upload image"}
              >
                <div className="aspect-[4/5] bg-black h-[150px] md:h-[170px] relative">
                  {step1UploadedAsset ? (
                    <img
                      className="object-cover w-full h-full"
                      src={step1UploadedAsset.url}
                      alt="Uploaded"
                      loading="lazy"
                      draggable={false}
                      onError={(e) => {
                        e.currentTarget.src = "/placeholder.jpg";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-[#151529]">
                      <div className="w-12 h-12 rounded-full bg-[#23233a] border border-[#2B2B4A] flex items-center justify-center mb-3">
                        <Plus size={22} className="text-white" />
                      </div>
                      <div className="text-white font-semibold text-sm">
                        Upload image
                      </div>
                      <div className="text-gray-400 text-[11px] mt-1 px-3 text-center">
                        JPG, PNG, WEBP
                      </div>
                    </div>
                  )}

                  {step1UploadLoading && (
                    <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                      <Loader2 className="animate-spin text-white" />
                    </div>
                  )}

                  <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                </div>

                {/* Actions (only when uploaded) */}
                {step1UploadedAsset && (
                  <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition">
                    <button
                      type="button"
                      className="px-2.5 py-1.5 rounded-md bg-black/70 hover:bg-black/85 text-white text-xs font-semibold border border-white/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (step1UploadLoading || imagesLoading) return;
                        step1FileInputRef.current?.click();
                      }}
                      aria-label="Replace uploaded image"
                      disabled={step1UploadLoading || imagesLoading}
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      className="p-2 rounded-md bg-black/70 hover:bg-black/85 text-white border border-white/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = step1UploadedAsset.url;
                        setStep1UploadedAsset(null);
                        if (selectedImage === url) setSelectedImage(null);
                      }}
                      aria-label="Remove uploaded image"
                      disabled={step1UploadLoading || imagesLoading}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>

              {/* Existing analyzed assets */}
              {normalizedImages.map((img, i) => (
                <div
                  key={`${img}-${i}`}
                  className={`relative rounded-lg overflow-hidden border-2 cursor-pointer group transition ${
                    selectedImage === img
                      ? "border-pink-500 ring-2 ring-pink-600"
                      : "border-transparent hover:border-pink-400"
                  }`}
                  onClick={() => setSelectedImage(img)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedImage(img);
                    }
                  }}
                >
                  <div className="aspect-[4/5] bg-black h-[150px] md:h-[170px]">
                    <img
                      src={img}
                      alt="Product"
                      className="object-cover w-full h-full"
                      loading="lazy"
                      draggable={false}
                      onError={(e) => {
                        e.currentTarget.src = "/placeholder.jpg";
                      }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                </div>
              ))}
            </div>
          )}

          {uploadError && !imagesLoading && (
            <div className="mt-3 text-red-400 text-sm">{uploadError}</div>
          )}

          {imagesError && !imagesLoading && (
            <div className="mt-3 text-red-400 text-sm">{imagesError}</div>
          )}

          {!hideSizeSelector && (
            <div className="mt-6">
              <p className="text-lg font-semibold mb-3">Select creative size</p>
              <AspectRatioPicker
                selected={selectedSize}
                onSelect={setSelectedSize}
              />
            </div>
          )}
        </div>

        <StepFooter onGenerate={handleGenerate} canGenerate={canGenerate} />
      </div>
    </Dialog>
  );
}
